import { useEffect, type ReactElement, type ReactNode } from 'react';
import {
    ActionIcon,
    Alert,
    Anchor,
    Badge,
    Box,
    Button,
    Center,
    FileButton,
    Group,
    Loader,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Textarea,
    Title,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Briefcase, Globe, MapPin, Sparkles, Trash2, Upload, Users } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import InitialAvatar from '@/design/InitialAvatar';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OrganizationSettingsViewModel } from './OrganizationSettingsViewModel';

const INDUSTRY_OPTIONS = [
    'Software & Technology',
    'Financial Services',
    'Healthcare',
    'Retail & E-commerce',
    'Manufacturing',
    'Education',
    'Government & Public Sector',
    'Other',
];

const REGION_OPTIONS = [
    { value: 'us-east', label: 'US East (N. Virginia)' },
    { value: 'us-west', label: 'US West (Oregon)' },
    { value: 'eu-central', label: 'EU Central (Frankfurt)' },
    { value: 'eu-west', label: 'EU West (Ireland)' },
    { value: 'ap-southeast', label: 'Asia Pacific (Singapore)' },
];

const SIZE_OPTIONS = ['1–50', '51–200', '201–1,000', '1,001–5,000', '5,000+'];

const DESCRIPTION_MAX = 600;

function Section({ title, description, children }: { title: string; description: string; children: ReactNode }): ReactElement {
    return (
        <Box>
            <Text size="sm" fw={600}>{title}</Text>
            <Text size="xs" c="dimmed" mb="sm">{description}</Text>
            {children}
        </Box>
    );
}

export default function OrganizationSettingsPage(): ReactElement {
    const vm = useViewModel(OrganizationSettingsViewModel);

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    const regionLabel = REGION_OPTIONS.find((r) => r.value === vm.region)?.label ?? null;

    const save = async (): Promise<void> => {
        const ok = await vm.save();
        if (ok) {
            notifications.show({ title: 'Organization saved', message: 'Your organization profile has been updated.', color: 'green' });
        }
    };

    return (
        <Box>
            <PageHeaderComponent
                title="Organization"
                subtitle="Company profile and context used across the workspace and AI features."
            />

            {vm.loadError && (
                <Alert color="red" title="Failed to load" mb="md">{vm.loadError}</Alert>
            )}

            {vm.loading ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : !vm.loadError && (
                <Stack gap="xl">
                    <Box
                        style={{
                            border: '1px solid var(--bm-color-border)',
                            borderRadius: 'var(--bm-radius)',
                            background: 'var(--bm-color-card)',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            style={{
                                height: 72,
                                background: 'linear-gradient(135deg, var(--mantine-primary-color-light), var(--mantine-primary-color-light-hover))',
                            }}
                        />
                        <Box px="lg" pb="lg">
                            <Group justify="space-between" align="flex-end" wrap="nowrap" gap="md">
                                <Box style={{ minWidth: 0 }}>
                                    <Box
                                        style={{
                                            width: 'fit-content',
                                            marginTop: -36,
                                            marginBottom: 'var(--mantine-spacing-sm)',
                                            border: '4px solid var(--bm-color-card)',
                                            borderRadius: 'calc(var(--bm-radius) + 4px)',
                                            background: 'var(--bm-color-body)',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {vm.logoUrl ? (
                                            <Box style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8, background: 'var(--bm-color-body)' }}>
                                                <img src={vm.logoUrl} alt="Company logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                            </Box>
                                        ) : (
                                            <InitialAvatar source={vm.name} size={64} variant="filled" />
                                        )}
                                    </Box>
                                    <Title order={3} fw={700} lineClamp={1}>{vm.name || 'Company name'}</Title>
                                    {vm.website && (
                                        <Anchor href={vm.website} target="_blank" size="xs" c="dimmed" lineClamp={1}>
                                            {/* arch-check-ignore: regex-literal slashes */ vm.website.replace(/^https?:\/\//, '')}
                                        </Anchor>
                                    )}
                                    <Group gap="xs" mt="md">
                                        {vm.industry && (
                                            <Badge variant="light" leftSection={<Briefcase size={11} />}>{vm.industry}</Badge>
                                        )}
                                        <Badge variant="light" color="gray" leftSection={<Users size={11} />}>
                                            {vm.memberCount.toLocaleString()} members
                                        </Badge>
                                        {regionLabel && (
                                            <Badge variant="light" color="gray" leftSection={<MapPin size={11} />}>{regionLabel}</Badge>
                                        )}
                                    </Group>
                                </Box>
                                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                                    <FileButton onChange={(file) => void vm.setLogoFile(file)} accept="image/png,image/jpeg,image/svg+xml">
                                        {(props) => (
                                            <Button {...props} variant="default" size="xs" leftSection={<Upload size={14} />}>
                                                {vm.logoUrl ? 'Replace logo' : 'Upload logo'}
                                            </Button>
                                        )}
                                    </FileButton>
                                    {vm.logoUrl && (
                                        <Tooltip label="Remove logo">
                                            <ActionIcon variant="subtle" color="red" size="lg" aria-label="Remove logo" onClick={() => void vm.setLogoFile(null)}>
                                                <Trash2 size={16} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                </Group>
                            </Group>
                        </Box>
                    </Box>

                    <SurfaceCard $padding="lg" $static>
                        <Stack gap="xl">
                            <Section title="Profile" description="How your organization is identified across the workspace.">
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" verticalSpacing="md">
                                    <TextInput label="Company name" value={vm.name} onChange={(e) => vm.setName(e.currentTarget.value)} placeholder="Acme Corporation" />
                                    <TextInput label="Website" leftSection={<Globe size={14} />} value={vm.website} onChange={(e) => vm.setWebsite(e.currentTarget.value)} />
                                </SimpleGrid>
                            </Section>

                            <Section title="Details" description="Industry, region, and size — used as context across the workspace.">
                                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" verticalSpacing="md">
                                    <Select label="Industry" leftSection={<Briefcase size={14} />} data={INDUSTRY_OPTIONS} value={vm.industry} onChange={(v) => vm.setIndustry(v)} />
                                    <Select label="Primary region" leftSection={<MapPin size={14} />} data={REGION_OPTIONS} value={vm.region} onChange={(v) => vm.setRegion(v)} />
                                    <Select label="Company size" leftSection={<Users size={14} />} data={SIZE_OPTIONS} value={vm.size} onChange={(v) => vm.setSize(v)} />
                                </SimpleGrid>
                            </Section>

                            <Box>
                                <Group gap="xs" mb="xs">
                                    <Text size="sm" fw={600}>Company description</Text>
                                    <Badge size="xs" variant="light" color="grape" leftSection={<Sparkles size={10} />}>AI context</Badge>
                                </Group>
                                <Text size="xs" c="dimmed" mb="sm">
                                    A short, plain-language summary of what your company does, its industry, and size.
                                    This is provided to AI features as context, so write it the way you would brief a new colleague.
                                </Text>
                                <Textarea
                                    aria-label="Company description"
                                    value={vm.description}
                                    onChange={(e) => vm.setDescription(e.currentTarget.value.slice(0, DESCRIPTION_MAX))}
                                    autosize
                                    minRows={4}
                                    maxRows={8}
                                />
                                <Text size="xs" c="dimmed" ta="right" mt={4}>{vm.description.length} / {DESCRIPTION_MAX}</Text>
                            </Box>
                        </Stack>
                    </SurfaceCard>

                    {vm.saveError && (
                        <Alert color="red" title="Save failed">{vm.saveError}</Alert>
                    )}

                    <Group>
                        <Button onClick={() => void save()} loading={vm.saving} disabled={!vm.isDirty && !vm.saving}>
                            Save changes
                        </Button>
                    </Group>
                </Stack>
            )}
        </Box>
    );
}
